export const findField = (obj, fieldName) => {
  if (!obj || typeof obj !== 'object') return null

  if (Object.prototype.hasOwnProperty.call(obj, fieldName)) {
    return obj[fieldName]
  }

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const result = findField(obj[key], fieldName)
      if (result) return result
    }
  }

  return null
}

export const DEFAULT_OG_IMAGE_PATH =
  'https://images.contentstack.io/v3/assets/blt56fad3014e7f45aa/blt6fe4d2f3bcc010fc/6616aebfcbc2fb4d84818018/HarborLogoPeopleEntry.png'

const getFirst = (value) => (Array.isArray(value) ? value[0] : value)

const toNonEmptyString = (value) => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const pickFirstString = (...values) => {
  for (const value of values) {
    const next = toNonEmptyString(value)
    if (next) return next
  }
  return undefined
}

export const resolveOpenGraphImage = (value, siteUrl) => {
  const image = pickFirstString(value, DEFAULT_OG_IMAGE_PATH)

  try {
    return new URL(image).toString()
  } catch {
    try {
      return new URL(image, siteUrl).toString()
    } catch {
      return image
    }
  }
}

const decodeHtmlEntities = (value) =>
  value
    .replace(/&(amp|apos|#39|gt|lt|nbsp|quot);/g, (entity) => {
      const entities = {
        '&amp;': '&',
        '&apos;': "'",
        '&#39;': "'",
        '&gt;': '>',
        '&lt;': '<',
        '&nbsp;': ' ',
        '&quot;': '"',
      }
      return entities[entity] || entity
    })
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))

const extractSeoText = (value) => {
  if (typeof value === 'string') {
    return decodeHtmlEntities(value.replace(/<[^>]*>/g, ' '))
      .replace(/\s+/g, ' ')
      .trim()
  }
  if (Array.isArray(value)) {
    return value.map(extractSeoText).filter(Boolean).join(' ').trim()
  }
  if (!value || typeof value !== 'object') return undefined
  if (typeof value.text === 'string') return extractSeoText(value.text)
  if (typeof value.html === 'string') return extractSeoText(value.html)
  return extractSeoText(value.children || value.content)
}

const getAuthoredTicker = (value) => {
  const match = toNonEmptyString(value)?.match(/\(([A-Z][A-Z0-9.-]{1,9})\)\s*$/)
  return match?.[1]
}

const resolveOgImageValue = (openGraphBlock) => {
  if (!openGraphBlock || typeof openGraphBlock !== 'object') return undefined
  const ogImageField = getFirst(
    openGraphBlock?.og_image ?? openGraphBlock?.image
  )
  const ogImageObject =
    ogImageField && typeof ogImageField === 'object' ? ogImageField : undefined
  const localAsset = getFirst(ogImageObject?.localAsset ?? ogImageObject?.asset)

  return pickFirstString(
    ogImageField,
    ogImageObject?.url,
    localAsset?.url,
    localAsset?.publicURL,
    localAsset?.childImageSharp?.resize?.src
  )
}

export function deriveSeoFromResult(result, pathname) {
  const ctx = result?.pageContext || {}
  const data = result?.data || {}

  const pickPrimary = () => {
    const vals = Object.values(data || {})
    const directCandidates = []
    const nestedCandidates = []

    for (const v of vals) {
      if (!v) continue
      if (typeof v === 'object' && !Array.isArray(v)) {
        directCandidates.push(v)
        if (Array.isArray(v.nodes)) {
          nestedCandidates.push(...v.nodes.filter(Boolean))
        }
      }
      if (Array.isArray(v)) {
        nestedCandidates.push(...v.filter(Boolean))
      }
    }

    const candidates = [...directCandidates, ...nestedCandidates]
    const metadataCandidate = candidates.find(
      (candidate) =>
        candidate &&
        typeof candidate === 'object' &&
        (Object.prototype.hasOwnProperty.call(candidate, 'seo') ||
          Object.prototype.hasOwnProperty.call(candidate, 'open_graph'))
    )
    if (metadataCandidate) return metadataCandidate

    const contentCandidate = candidates.find(
      (candidate) =>
        candidate &&
        (candidate.user_experience || candidate.title || candidate.description)
    )
    if (contentCandidate) return contentCandidate

    return vals.length === 1 ? vals[0] : {}
  }

  const primary = pickPrimary() || {}
  const seoBlock = primary?.seo || ctx?.seo || {}
  const ogBlock = primary?.open_graph || ctx?.open_graph || {}
  const heroBlock = Array.isArray(primary?.hero)
    ? primary.hero.find(Boolean) || {}
    : primary?.hero || {}
  const ux = primary?.user_experience || ctx?.user_experience
  const uxValue =
    typeof ux === 'string'
      ? ux
      : ux && ux.experience
        ? ux.experience
        : undefined
  const routeTicker = toNonEmptyString(ctx?.ticker)?.toUpperCase()
  const authoredTicker = getAuthoredTicker(seoBlock?.meta_title)
  const hasProductSeoMismatch = Boolean(
    routeTicker && authoredTicker && routeTicker !== authoredTicker
  )
  const productIdentityDescription =
    hasProductSeoMismatch && primary?.title
      ? `${primary.title} (${routeTicker}) investment product information from Harbor Capital Advisors.`
      : undefined

  const title =
    ctx.title ??
    (hasProductSeoMismatch
      ? `${primary?.title || routeTicker} (${routeTicker})`
      : seoBlock?.meta_title) ??
    primary?.title ??
    undefined

  const description = [
    ctx.description,
    !hasProductSeoMismatch ? seoBlock?.meta_description : undefined,
    productIdentityDescription,
    !hasProductSeoMismatch ? primary?.short_description : undefined,
    !hasProductSeoMismatch ? primary?.hero_text : undefined,
    !hasProductSeoMismatch ? heroBlock?.suite_description : undefined,
    !hasProductSeoMismatch ? heroBlock?.description : undefined,
    !hasProductSeoMismatch ? heroBlock?.subheadline : undefined,
    !hasProductSeoMismatch ? primary?.description : undefined,
  ]
    .map(extractSeoText)
    .find(Boolean)

  const keywords = hasProductSeoMismatch
    ? undefined
    : (seoBlock?.meta_keywords ?? undefined)

  const customMetaTagsRaw = Array.isArray(seoBlock?.custom_meta_tags)
    ? seoBlock.custom_meta_tags
    : []
  const authoredRobots = customMetaTagsRaw.find(
    (tag) => String(tag?.meta_key || '').toLowerCase() === 'robots'
  )?.meta_value
  const isErrorPath = /^\/(403|404|500)(?:\.html)?\/?$/.test(pathname || '')
  const robots =
    ctx?.isAuthenticated || isErrorPath
      ? 'noindex,nofollow'
      : ctx?.robots ||
        authoredRobots ||
        'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
  const customMetaTags = customMetaTagsRaw
    .map((x) => ({ name: x?.meta_key, content: x?.meta_value }))
    .filter(
      (x) =>
        x.name &&
        x.content &&
        !['description', 'keywords', 'data-institutional', 'robots'].includes(
          x.name.toLowerCase()
        )
    )

  const openGraph = {
    type: pickFirstString(
      ogBlock?.type,
      ogBlock?.og_type,
      ['Contentstack_insight_article', 'Contentstack_news_article'].includes(
        primary?.__typename
      )
        ? 'article'
        : 'website'
    ),
    title: pickFirstString(
      !hasProductSeoMismatch ? ogBlock?.title : undefined,
      !hasProductSeoMismatch ? ogBlock?.og_title : undefined,
      title
    ),
    description: pickFirstString(
      !hasProductSeoMismatch ? ogBlock?.description : undefined,
      !hasProductSeoMismatch ? ogBlock?.og_description : undefined,
      description
    ),
    url: pickFirstString(ogBlock?.url, ogBlock?.og_url, ctx?.url),
    image: resolveOgImageValue(ogBlock),
  }

  const institutional = uxValue === 'institutional-only' ? true : undefined

  return {
    title,
    description,
    keywords,
    customMetaTags,
    openGraph,
    institutional,
    pathname,
    robots,
    hasProductSeoMismatch,
  }
}

export function brandTitle(base, brand) {
  if (!base && brand) return brand
  if (!base) return ''
  const suffix = ` | ${brand}`
  return base.endsWith(suffix) ? base : `${base}${suffix}`
}
